import React, { useState, useEffect } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { useVsiAppContext } from "../stores/VsiApp";
import styles from "../utils/themeStyles";
import { genericColors } from "../utils/chartUtils";
import { withErrorBoundary } from "../boundaries/withErrorBoundary";
import GeneralErrorBoundary from "../boundaries/GeneralErrorBoundary";

require("highcharts/indicators/indicators")(Highcharts);
require("highcharts/indicators/pivot-points")(Highcharts);
require("highcharts/indicators/macd")(Highcharts);
require("highcharts/modules/exporting")(Highcharts);
require("highcharts/modules/map")(Highcharts);
require("highcharts/modules/dumbbell")(Highcharts);


function DumbbellSeries({ series, data, includeLegend, title = "", height, xTitle, yTitle, formatPoint, scrollHeight }) {
    const { theme, isTabletScreen } = useVsiAppContext();
    const basicTextStyle = {
        color: styles[theme].textColor,
        fontFamily: styles[theme].fontFamily,
    };

    const updateSeries = (series, data) => {
        if (series) return series;
        else if (data)
            return [
                {
                    data,
                },
            ];
        return [];
    };

    const [chartOptions, setChartOptions] = useState(() => {
        return {
            credits: {
                enabled: false
            },
            mapNavigation: {
                enabled: false,
            },
            xAxis: {
                type: 'category',
                title: {
                    fill: styles[theme].textColor,
                    text: xTitle
                },
                labels: {
                    style: basicTextStyle,
                    step: 1
                },
                scrollbar: {
                    enabled: true
                },
                maxPadding: 100
            },
            yAxis: {
                title: {
                    color: styles[theme].textColor,
                    text: yTitle
                },
				labels: {
					style: basicTextStyle,
					allowOverlap: true
				}
            },
            colors: genericColors,
            chart: {
                backgroundColor: styles[theme].cardBgColor,
                color: styles[theme].textColor,
                type: 'dumbbell',
                inverted: true,
                height: height || 500,
                scrollablePlotArea: {
                    color: styles[theme].textColor,
                    minHeight: scrollHeight
                },
                events: {
                    load() {
                        this.showLoading();
                        setTimeout(this.hideLoading.bind(this), 2000);
                    }
                }
            },
            plotOptions: {
                dumbbell: {
                    grouping: false
                }
            },
            title: {
                text: title,
                style: basicTextStyle
            },
            tooltip: {
                backgroundColor: styles[theme].altBgColor,
                style: basicTextStyle
            },
            legend: {
                itemStyle: basicTextStyle,
                align: "center",
                enabled: includeLegend ? true : false
            },
            series: updateSeries(series, data),
        }
    });

    useEffect(() => {
        setChartOptions(() => {
            return {
                ...chartOptions,
                style: {
                    backgroundColor: styles[theme].cardBgColor,
                },
                xAxis: {
                    ...chartOptions.xAxis,
                    type: 'category',
                    title: {
                        style: {
                            color: styles[theme].textColor,
                        },
                        text: xTitle
                    },
                    labels: {
                        style: basicTextStyle,
                        step: 0.5
                    },
                    min: 0,
                    scrollbar: {
                        enabled: true
                    },
                },
                yAxis: {
                    ...chartOptions.yAxis,
                    title: {
                        style: {
                            color: styles[theme].textColor,
                        },
                        text: yTitle
                    },
                    labels: {
                        style: basicTextStyle,
                        allowOverlap: true
                    }
                },
                chart: {
                    ...chartOptions.chart,
                    backgroundColor: styles[theme].cardBgColor,
					scrollablePlotArea: {
                        color: styles[theme].textColor,
                        minHeight: scrollHeight
					},
                    height: height || 500,
                    events: {
                        load() {
                            this.showLoading();
                            setTimeout(this.hideLoading.bind(this), 2000);
                        }
                    }
                },
                tooltip: {
                    ...chartOptions.tooltip,
                    pointFormat: formatPoint,
                },
                legend: {
                    ...chartOptions.legend,
                    align: "center"
                },
                series: updateSeries(series, data)
            }
        })
    }, [theme, isTabletScreen, series]);

    return <HighchartsReact highcharts={Highcharts} options={chartOptions} />;
}

DumbbellSeries.displayName = "DumbbellSeries"

export default withErrorBoundary(DumbbellSeries, GeneralErrorBoundary);
